import React from 'react';
import StudentSearch from './components/Teacher/StudentSearch';
import NativeReports from './components/Student/NativeReports/NativeReports';
import ReportingPortal from './components/Reports/index';
import CorrectionManager from './components/Teacher/CorrectionManager';
import ComprehensionReportCard from './components/Teacher/ComprehensionReportCard/ComprehensionReportCard';
import AdminPortal from './components/Teacher/Admin/AdminPortal';
import CustomBenchmark from './components/Teacher/Admin/containers/CustomBenchmark';
import BenchmarkAssessment from './components/Teacher/Admin/containers/BenchmarkAssessment';
import SchoolResults from './components/Teacher/Admin/containers/SchoolResults';
import AdminDashboard from './components/Teacher/Admin/containers/AdminDashboard';
import Resources from './components/Teacher/Resources';
import config from './config';
import category from './routesCategory';
import ConfigurationView from './components/Teacher/Admin/containers/ConfigurationView';
import ScienceOfReadingConfig from './components/Teacher/Admin/containers/ScienceOfReadingConfig';
import BadgingConfig from './components/Teacher/Admin/containers/BadgingConfig';
import { Route } from 'react-router-dom';
import { ReportAnalytics } from './components/Reports/blocks/ReportAnalytics';

/*
***************************** ROUTES ********************************************
**
** const category:
** Think of category as the views/pages of our app organized by userType.
**
** const routeStore:
** The routeStore object contains all the possible routes of the App.
** Routes are organized by user type, so we have routeStore.student
** for userType student and we have routeStore.teacher for teachers.
**
** Each userType has a DEFAULT route that will be used to redirect our 404's match
** and the IntentManager to redirect to our Home Page via ChatBot.
**
** We have a routes property containing an array of Route objects with all the properties
** necessary to create a route + any other property that you wish to pass to the component
** being rendered in that Route.
**
** Each Route object contains a category that's used to match Intent Names with our Views/Categories
**
** --- Receiving Params from ChatBot ---
**
** If a certain path/route uses URL parameters, we need to create a
** matchRouteWithSlots({...expected_params_from_chatBot}) method to do the mapping
** of the ChatBot's parameters with our Route's Parameter.
**
** e.g.:
{
  //Rest of the route properties (category, path, component...)
  matchRouteWithSlots: function({activityId='1F8783C4D2ED11E89543168CA1AFEB4A'}){
    return this.path.replace(':activityId', activityId);
  },
}
** In this example we're expecting the answer from the ChatBot to contain an "activityId" property, if for any
** reason this property is coming back as chatBot_activity_id then we would have to change our code to be:
{
  //Rest of the route properties (category, path, component...)
  matchRouteWithSlots: function({chatBot_activity_id='1F8783C4D2ED11E89543168CA1AFEB4A'}){
    return getCleanPath(this.path).replace(':activityId', chatBot_activity_id);
  },
}
** --- IMPORTANT ---
**
** make sure to include the : when replacing a parameter with the desired value
    getCleanPath(this.path).replace(':activityId', chatBot_activity_id);
    //-----------------------------> ^
*/

export const routeStore = {
  teacher: {
    DEFAULT: '/teacher/reports',
    routes: [
      {
        category: category.teacher.ACTIVITIES,
        path: '/teacher/gradebook/activities',
        component: StudentSearch,
        config: config.studentSearch,
        title: 'Activities',
      },
      {
        category: category.teacher.REPORTS,
        path: '/teacher/reports/type/:report',
        component: ReportingPortal,
        config: config.teacher,
        exact: true,
      },
      {
        category: category.teacher.REPORTS,
        path: '/teacher/reports',
        component: ReportingPortal,
        config: config.teacher,
        exact: true,
        title: 'Reports',
      },
      {
        //category: category.teacher.GRADEBOOK, TODO: Add the relevant Category when we have an Intent for this route
        path: '/teacher/gradebook/scoring/:activityId',
        component: CorrectionManager,
        config: config.teacher,
        //TODO: Implement matchRouteWithSlots
        //matchRouteWithSlots: function({activityId='1F8783C4D2ED11E89543168CA1AFEB4A'}){
          //activityId variable should match the name of the property sent from chatBot under slots
          //Inserting some dummy data as default to be safe
          //return this.path.replace(':activityId', activityId);
        //},
      },
      {
        //category: category.teacher.GRADEBOOK, TODO: Add the relevant Category when we have an Intent for this route
        path: '/teacher/gradebook/comprehension',
        component: ComprehensionReportCard,
        config: config.teacher,
      },
      {
        category: category.teacher.DIAGNOSTIC,
        path: '/teacher/resources',
        component: Resources,
        config: config.teacher,
        exact: true,
        title: 'Resources',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/school',
        component: SchoolResults,
        config: config.teacher,
        exact: true,
        type: 'schoolResults',
        label: 'SCHOOL-WIDE RESULTS',
        subtext: 'Export school-wide test results',
        tagline: 'CSV-format data on all students',
        link: '/admin/school',
        title: 'School-wide Results',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/dashboard/:itemId',
        component: AdminDashboard,
        config: config.teacher,
        type: 'adminDashboard',
        label: 'ADMIN DASHBOARD',
        title: 'Admin Dashboard',
        subtext: 'View aggregate data for your district or school',
        tagline: ' ',
        link: '/admin/dashboard/tracking',
        subdistrict: true,
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin',
        component: AdminPortal,
        config: config.teacher,
        exact: true,
        title: 'Admin Portal',
      },
      /*{
        category: category.teacher.REPORTS,
        path: '/teacher/reports/:reportType/:reportId',
        component: ReportingPortal,
        config: config.teacher,
        matchRouteWithSlots: function({reportType='benchmark', reportId='12345'}){
          //reportType and reportId variables should match the name of the properties sent from chatBot under slots
          //Inserting some dummy data as default to be safe
          return this.path.replace(':reportType', reportType).replace(':reportId', reportId);
        },
      },*/
    ],
  },
  student: {
    DEFAULT: '/student/home',
    routes: [
      {
        category: category.student.STUDENTHOME,
        path: '/student/home',
        //Rendered on App.js
        config: config.studentHome,
      },
      {
        category: category.student.TUTOR,
        path: '/student/tutor',
        //Rendered on App.js
        config: config.student,
      },
      {
        category: category.student.TUTOR,
        path: '/student/test',
        //Rendered on App.js
        config: config.studentTest,
      },
      {
        category: category.student.ASSESSMENT,
        path: '/student/assessment',
        //Rendered on App.js
        config: config.assessment,
      },
      {
        category: category.student.AUDIOCONTROLS,
        path: null,
      },
      {
        category: category.student.INTERVENTION,
        path: null,
      },
      {
        category: category.student.HELP,
        path: '/student/help',
      },
      {
        category: category.student.LOGIN,
        path: null,//not handled by react router
      },
      {
        category: category.student.REPORT,
        path: '/student/reports/:reportType/:reportId',
      },
      {
        category: category.student.RECOMMENDATION,
        path: '/student/recommendation',
      },
      {
        category: category.teacher.ACTIVITIES,
        path: '/teacher/activities',
        component: StudentSearch,
        config: config.teacher,
      },
      {
        category: category.teacher.GRADEBOOK,
        path: '/teacher/scoring/:activityId',
        component: CorrectionManager,
        config: config.teacher,
        matchRouteWithSlots: function({activityId='1F8783C4D2ED11E89543168CA1AFEB4A'}){
          //activityId variable should match the name of the property sent from chatBot under slots
          //Inserting some dummy data as default to be safe
          return this.path.replace(':activityId', activityId);
        },
      },
      {
        category: category.teacher.REPORTS,
        path: '/teacher/reports/:reportType/:reportId',
        component: NativeReports,
        config: config.teacher,
        matchRouteWithSlots: function({reportType='benchmark', reportId='12345'}){
          //reportType and reportId variables should match the name of the properties sent from chatBot under slots
          //Inserting some dummy data as default to be safe
          return this.path.replace(':reportType', reportType).replace(':reportId', reportId);
        },
      },
      {
        category: category.student.REPORTS,
        path: '/student/reports/:reportType/:reportId',
        component: NativeReports,
        config: config.student,
        matchRouteWithSlots: function({reportType='progress', reportId='12345'}){
          //reportType and reportId variables should match the name of the properties sent from chatBot under slots
          //Inserting some dummy data as default to be safe
          return this.path.replace(':reportType', reportType).replace(':reportId', reportId);
        },
      },
    ],
  },
  DISTRICT_ADMIN: {
    DEFAULT: '/admin',
    routes: [
      {
        category: category.teacher.ADMIN,
        path: '/admin/benchmark',
        component: CustomBenchmark,
        config: config.teacher,
        exact: true,
        type: 'customBenchmark',
        label: 'CUSTOM BENCHMARK',
        title: 'Custom Benchmark',
        subtext: 'Enter cut lines for your district',
        tagline: 'cut lines, by grade and season',
        link: '/admin/benchmark',
        subdistrict: false,
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/school',
        component: SchoolResults,
        config: config.teacher,
        exact: true,
        type: 'schoolResults',
        label: 'DISTRICT-WIDE RESULTS',
        title: 'District-wide Results',
        subtext: 'Export district-wide test results',
        tagline: 'CSV-format data on all students',
        link: '/admin/school',
        subdistrict: true
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/benchmark-assessment',
        component: BenchmarkAssessment,
        config: config.teacher,
        exact: true,
        type: 'benchmarkAssessment',
        label: 'ENABLE BENCHMARK ASSESSMENT',
        title: 'Enable Benchmark Assessment',
        subtext: 'Assign an assessment to all students in your district',
        tagline: ' ',
        link: '/admin/benchmark-assessment',
        subdistrict: true
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/view-configuration',
        component: ConfigurationView,
        config: config.teacher,
        exact: true,
        type: 'viewConfiguration',
        label: 'CONFIGURATION SETTINGS',
        title: 'Configuration Settings',
        subtext: 'View and update the assessment and screener tasks',
        tagline: ' ',
        link: '/admin/view-configuration',
        subdistrict: true
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/dashboard/:itemId',
        component: AdminDashboard,
        config: config.teacher,
        type: 'adminDashboard',
        label: 'ADMIN DASHBOARD',
        title: 'Admin Dashboard',
        subtext: 'View aggregate data for your district or school',
        tagline: ' ',
        link: '/admin/dashboard/tracking',
        subdistrict: true
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/science_of_reading_config',
        component: ScienceOfReadingConfig,
        config: config.teacher,
        exact: true,
        type: 'readingConfig',
        label: 'SCIENCE OF READING CONFIGURATION',
        title: 'Science of Reading Configuration',
        subtext: 'Configure the student practice experience. Select the scales and standards available in teacher reports',
        tagline: ' ',
        link: '/admin/science_of_reading_config',
        subdistrict: true
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/badging-configuration',
        component: BadgingConfig,
        config: config.teacher,
        exact: true,
        type: 'badgingConfig',
        label: 'BADGING CONFIGURATION',
        title: 'Badging Configuration',
        subtext: 'Enable or disable badging for your district or school',
        tagline: ' ',
        link: '/admin/badging-configuration',
        subdistrict: true,
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin',
        component: AdminPortal,
        title: 'Admin Portal',
        config: config.teacher,
        exact: true,
        subdistrict: true
      },
      {
        category: category.teacher.ACTIVITIES,
        path: '/teacher/gradebook/activities',
        component: StudentSearch,
        title: 'Activities',
        config: config.studentSearch,
        subdistrict: true
      },
      {
        category: category.teacher.REPORTS,
        path: '/teacher/reports/type/:report',
        component: ReportingPortal,
        config: config.teacher,
        exact: true,
        subdistrict: true
      },
      {
        category: category.teacher.REPORTS,
        path: '/teacher/reports',
        component: ReportingPortal,
        title: 'Reports',
        config: config.teacher,
        exact: true,
        subdistrict: true
      },
      {
        path: '/teacher/gradebook/scoring/:activityId',
        component: CorrectionManager,
        config: config.teacher,
        subdistrict: true
      },
      {
        //category: category.teacher.GRADEBOOK, TODO: Add the relevant Category when we have an Intent for this route
        path: '/teacher/gradebook/comprehension',
        component: ComprehensionReportCard,
        config: config.teacher,
      },
    ]
  },
  SCHOOL_ADMIN: {
    DEFAULT: '/admin',
    routes: [
      {
        category: category.teacher.ADMIN,
        path: '/admin/benchmark',
        component: CustomBenchmark,
        config: config.teacher,
        exact: true,
        type: 'customBenchmark',
        label: 'CUSTOM BENCHMARK',
        title: 'Custom Benchmark',
        subtext: 'Enter cut lines for your district',
        tagline: 'cut lines, by grade and season',
        link: '/admin/benchmark',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/school',
        component: SchoolResults,
        config: config.teacher,
        exact: true,
        type: 'schoolResults',
        label: 'SCHOOL-WIDE RESULTS',
        title: 'School-wide Results',
        subtext: 'Export school-wide test results',
        tagline: 'CSV-format data on all students',
        link: '/admin/school'
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/benchmark-assessment',
        component: BenchmarkAssessment,
        config: config.teacher,
        exact: true,
        type: 'benchmarkAssessment',
        label: 'ENABLE BENCHMARK ASSESSMENT',
        title: 'Enable Benchmark Assessment',
        subtext: 'Assign an assessment to all students in your district',
        tagline: ' ',
        link: '/admin/benchmark-assessment',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/view-configuration',
        component: ConfigurationView,
        config: config.teacher,
        exact: true,
        type: 'viewConfiguration',
        label: 'CONFIGURATION SETTINGS',
        title: 'Configuration Settings',
        subtext: 'View and update the assessment and screener tasks',
        tagline: ' ',
        link: '/admin/view-configuration',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/dashboard/:itemId',
        component: AdminDashboard,
        config: config.teacher,
        type: 'adminDashboard',
        label: 'ADMIN DASHBOARD',
        title: 'Admin Dashboard',
        subtext: 'View aggregate data for your district or school',
        tagline: ' ',
        link: '/admin/dashboard/tracking',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/science_of_reading_config',
        component: ScienceOfReadingConfig,
        config: config.teacher,
        exact: true,
        type: 'readingConfig',
        label: 'SCIENCE OF READING CONFIGURATION',
        title: 'Science of Reading Configuration',
        subtext: 'Configure the student practice experience. Select the scales and standards available in teacher reports',
        tagline: ' ',
        link: '/admin/science_of_reading_config',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin/badging-configuration',
        component: BadgingConfig,
        config: config.teacher,
        exact: true,
        type: 'badgingConfig',
        label: 'BADGING CONFIGURATION',
        title: 'Badging Configuration',
        subtext: 'Enable or disable badging for your district or school',
        tagline: ' ',
        link: '/admin/badging-configuration',
      },
      {
        category: category.teacher.ADMIN,
        path: '/admin',
        component: AdminPortal,
        title: 'Admin Portal',
        config: config.teacher,
        exact: true,
      },
      {
        category: category.teacher.ACTIVITIES,
        path: '/teacher/gradebook/activities',
        component: StudentSearch,
        title: 'Activities',
        config: config.studentSearch,
      },
      {
        category: category.teacher.REPORTS,
        path: '/teacher/reports/type/:report',
        component: ReportingPortal,
        config: config.teacher,
        exact: true,
      },
      {
        category: category.teacher.REPORTS,
        path: '/teacher/reports',
        component: ReportingPortal,
        title: 'Reports',
        config: config.teacher,
        exact: true,
      },
      {
        path: '/teacher/gradebook/scoring/:activityId',
        component: CorrectionManager,
        config: config.teacher,
      },
      {
        //category: category.teacher.GRADEBOOK, TODO: Add the relevant Category when we have an Intent for this route
        path: '/teacher/gradebook/comprehension',
        component: ComprehensionReportCard,
        config: config.teacher,
      },
    ],
  }
};

export function RouteComponentRenderer(props) {
  const { location, path, title } = props;
  const Component = props.component;
  const loggedPath = location?.pathname || path;
  const passthroughProps = { ...props }
  return (
    <Route
      path={path}
      render={(props) => <ReportAnalytics path={loggedPath} title={title}>
        <Component {...props} {...passthroughProps}/>
      </ReportAnalytics>}
    />
  );
}